import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { BlockUI, NgBlockUI } from "ng-block-ui";
import { MessageService } from "primeng/api";
import { of } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { ConnectionActions } from "../actions/connection.actions";

@Injectable()
export class ConnectionsEffects {
  @BlockUI() blockUI!: NgBlockUI;
  private messageService = inject(MessageService);

  constructor(private actions$: Actions, private restService: RestService, private readonly toaster: ToasterService) { }

  createConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectionActions.createConnection),
      mergeMap((action) =>
        this.restService.createConnection(action.id, action.connection).pipe(
          map((connection: any) => {
            this.toaster.pop({ type: 'success', text: MessageEnum.CONNECTION_CREATE_SUCCESS });
            return ConnectionActions.createConnectionSuccess({
              connection: connection?.data,
            });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: error?.message ? error?.message : MessageEnum.CONNECTION_CREATE_FAILURE });
            return of(ConnectionActions.createConnectionFailure({ error }))
          }

          )
        )
      )
    )
  );

  getConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectionActions.getConnections),
      mergeMap((action) =>
        this.restService.getConnections(action.id).pipe(
          map((response) => {
            const connections = response.data;
            return ConnectionActions.getConnectionSuccess({
              connection: connections,
            });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.CONNECTION_LOAD_FAILURE });
            return of(ConnectionActions.getConnectionFailure({ error }));
          }

          )
        )
      )
    )
  );

  updateConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectionActions.updateConnection),
      mergeMap((action) =>
        this.restService
          .updateConnection(action.projectId, action.connId, action.connection)
          .pipe(
            map((connection: any) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.CONNECTION_UPDATE_SUCCESS });
              return ConnectionActions.updateConnectionSuccess({
                connection: connection?.data,
              });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.CONNECTION_UPDATE_FAILURE });
              return of(ConnectionActions.updateConnectionFailure({ error }))
            }
            )
          )
      )
    )
  );

  deleteConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectionActions.deleteConnection),
      mergeMap((action) =>
        this.restService.deleteConnection(action.projectId, action.connId).pipe(
          map(() => {
            this.toaster.pop({ type: 'success', text: MessageEnum.CONNECTION_DELETE_SUCCESS });
            return ConnectionActions.deleteConnectionSuccess({
              connection: action.connId,
            });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.CONNECTION_DELETE_FAILURE });
            return of(ConnectionActions.deleteConnectionFailure({ error }));
          })
        )
      )
    )
  );

  createFileConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConnectionActions.createFileConnection),
      mergeMap((action) =>
        this.restService
          .createFileConnection(action.id, action.connection)
          .pipe(
            map((connection: any) =>
              ConnectionActions.createFileConnectionSuccess({
                connection: connection?.data,
              })
            ),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.FILE_CONNECTION_CREATE_FAILURE });
              return of(ConnectionActions.createFileConnectionFailure({ error }))
            }
            )
          )
      )
    )
  );
}
